"use client"

import { useConversationVolumeQuery } from "@/hooks/queries/conversation-quality"
import type { ConversationVolumeResult } from "@/server-actions/conversation-quality"

import { MissedReplies } from "../sections/missed-reply-sections"
import {
  BusiestHours,
  ChannelBreakdown,
  ReturningCustomers,
  SentimentBreakdown,
  SessionsByDay,
  VolumeKpis,
} from "../sections/volume-sections"
import { ErrorAlert, Notice, useLanding, type QualityTabProps } from "./tab-parts"
import { useTranslations } from "next-intl"


/** How much came in, where from and when, and what the agent left unanswered. */
export function VolumeTab({ input, ready }: QualityTabProps) {
  const tUi = useTranslations("ConversationQuality.ConversationQualityTabsVolumeTab")

  const { data, error } = useConversationVolumeQuery(input, ready)
  // `missed=<cause>` lands on that row opened, for a link that means one cause.
  const [openCause, clearOpenCause] = useLanding("missed")

  if (error) return <ErrorAlert error={error} />
  if (data && !hasAnyActivity(data)) {
    return <Notice><span key={tUi("noSessionsStartedInThisPeriodForThis")}>{tUi("noSessionsStartedInThisPeriodForThis")}</span></Notice>
  }

  return (
    <div className="space-y-3">
      <VolumeKpis data={data} />
      <SessionsByDay data={data} />
      <div className="grid gap-3 md:grid-cols-[7fr_5fr] md:items-start">
        <ChannelBreakdown data={data} />
        <SentimentBreakdown data={data} />
      </div>
      <div className="grid gap-3 lg:grid-cols-[7fr_5fr] lg:items-start">
        <BusiestHours data={data} />
        <ReturningCustomers data={data} />
      </div>
      <MissedReplies data={data} input={input} openCause={openCause} onOpened={clearOpenCause} />
    </div>
  )
}

/**
 * Whether the window holds anything worth drawing.
 *
 * **Every aggregate keyed independently of `conversation_sessions` must be listed here.** Three
 * currently are: messages and markers carry their own timestamps, so a session opened before
 * the window still produces activity inside it, and a non-actionable inbound — a story
 * mention, a sticker — writes a `reply_skipped` marker without opening a session at all and
 * without counting as a message. Checking sessions alone hides both.
 */
function hasAnyActivity(data: ConversationVolumeResult): boolean {
  return (
    data.counts.sessions > 0 ||
    data.counts.messages > 0 ||
    data.missedReplies.length > 0 ||
    data.prevCounts.sessions > 0 ||
    data.prevCounts.messages > 0
  )
}
